import { memo, useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const COLORS = {
  number: "#1B6E3C", // deep green — Number series
  area: "#8FCB3E", // lime — Area series
  areaText: "#6FA32B", // darker lime for text (axis ticks / bar labels)
  ink: "#1C2A22",
  sub: "#6B7A70",
  grid: "#E8EEE9",
  focusRing: "#134D2A",
};

const DIM_OPACITY = 0.25;
const ZERO_OPACITY = 0.25;

type SeriesKey = "number" | "area";

type ChartRow = {
  status: string;
  number: number;
  area: number;
};

type StatusBarChartProps = {
  data: { name: string; uv: number; pv: number }[];
  label: string;
};

const SERIES: {
  key: SeriesKey;
  label: string;
  yAxisId: string;
  color: string;
  labelColor: string;
  showZeroLabel: boolean;
}[] = [
  {
    key: "number",
    label: "Number",
    yAxisId: "num",
    color: COLORS.number,
    labelColor: COLORS.number,
    showZeroLabel: true,
  },
  {
    key: "area",
    label: "Area",
    yAxisId: "area",
    color: COLORS.area,
    labelColor: COLORS.areaText,
    showZeroLabel: false, // avoid a doubled "0 0" on empty statuses
  },
];

const fmt = (value: number) => (Number(value) || 0).toLocaleString("en-IN");

const sharePct = (value: number, base?: number) =>
  base ? `${Math.round((value / base) * 100)}%` : "";

/* Wrapping tick for long status names */
const WrapTick = ({ x, y, payload }: any) => {
  const words = String(payload.value).split(" ");
  const lines: string[] = [];
  let line = "";
  words.forEach((word) => {
    if ((line + " " + word).trim().length > 10) {
      lines.push(line.trim());
      line = word;
    } else {
      line = (line + " " + word).trim();
    }
  });
  if (line) lines.push(line);

  return (
    <g transform={`translate(${x},${y + 8})`}>
      {lines.map((text, index) => (
        <text
          key={index}
          y={index * 13}
          textAnchor="middle"
          fontSize={11}
          fill={COLORS.ink}
          fontWeight={500}
        >
          {text}
        </text>
      ))}
    </g>
  );
};

const TooltipRow = ({
  swatch,
  name,
  value,
  pct,
}: {
  swatch: string;
  name: string;
  value: string;
  pct: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      lineHeight: "20px",
    }}
  >
    <Box
      component="span"
      sx={{
        width: "9px",
        height: "9px",
        borderRadius: "3px",
        backgroundColor: swatch,
        flexShrink: 0,
      }}
    />
    <Box component="span" sx={{ color: "#DCE7E0" }}>
      {name}
    </Box>
    <Box component="span" sx={{ marginLeft: "auto", fontWeight: 700 }}>
      {value}
    </Box>
    <Box
      component="span"
      sx={{ color: "#8FA89A", width: "40px", textAlign: "right" }}
    >
      {pct}
    </Box>
  </Box>
);

const ChartTooltip = ({ active, payload, label, baseline, visible }: any) => {
  if (!active || !payload?.length) return null;
  const row: ChartRow = payload[0]?.payload;

  return (
    <Box
      sx={{
        backgroundColor: "#122B1C",
        color: "#fff",
        borderRadius: "12px",
        padding: "10px 14px",
        fontSize: "12px",
        minWidth: "200px",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
      }}
    >
      <Box sx={{ color: "#A8D5BA", marginBottom: "6px", fontWeight: 600 }}>
        {label}
      </Box>
      {visible.number && (
        <TooltipRow
          swatch={COLORS.number}
          name="Number"
          value={fmt(row.number)}
          pct={sharePct(row.number, baseline?.number)}
        />
      )}
      {visible.area && (
        <TooltipRow
          swatch={COLORS.area}
          name="Area (ha)"
          value={fmt(row.area)}
          pct={sharePct(row.area, baseline?.area)}
        />
      )}
    </Box>
  );
};

const StatusBarChart = ({ data, label }: StatusBarChartProps) => {
  const [visible, setVisible] = useState<Record<SeriesKey, boolean>>({
    number: true,
    area: true,
  });
  const [focused, setFocused] = useState<string | null>(null);

  const rows = useMemo<ChartRow[]>(
    () =>
      (data ?? []).map((item) => ({
        status: item.name,
        number: Number(item.uv) || 0,
        area: Number(item.pv) || 0,
      })),
    [data]
  );

  // first row is the "All Selected" bucket — the % base in the tooltip
  const baseline = rows[0];

  const zeroRows = useMemo(
    () => rows.filter((row) => !row.number && !row.area),
    [rows]
  );

  const toggleSeries = useCallback((key: SeriesKey) => {
    setVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      // never allow both series off
      return next.number || next.area ? next : prev;
    });
  }, []);

  const handleChartClick = useCallback((event: any) => {
    const status = event?.activeLabel;
    if (status) setFocused((prev) => (prev === status ? null : status));
  }, []);

  if (!rows.length) return null;

  const opacityFor = (row: ChartRow, value: number) => {
    if (value === 0) return ZERO_OPACITY;
    return focused === null || focused === row.status ? 1 : DIM_OPACITY;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: `1px solid ${COLORS.grid}`,
        borderRadius: "16px",
        padding: "20px",
        color: COLORS.ink,
      }}
    >
      {/* header: title + interactive legend chips */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
          marginBottom: "4px",
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: "16px", fontWeight: 700, color: COLORS.ink }}
          >
            {label}
          </Typography>
          {baseline && (
            <Typography
              sx={{ fontSize: "12px", color: COLORS.sub, marginTop: "2px" }}
            >
              {baseline.status}: {fmt(baseline.number)} · {fmt(baseline.area)}{" "}
              ha — click a bar to focus, legend to toggle
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: "8px" }}>
          {SERIES.map((series) => {
            const isOn = visible[series.key];
            return (
              <Box
                key={series.key}
                component="button"
                type="button"
                onClick={() => toggleSeries(series.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "5px 12px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  border: `1.5px solid ${isOn ? series.color : COLORS.grid}`,
                  backgroundColor: isOn ? `${series.color}18` : "transparent",
                  color: isOn ? series.labelColor : COLORS.sub,
                  textDecoration: isOn ? "none" : "line-through",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: "9px",
                    height: "9px",
                    borderRadius: "3px",
                    backgroundColor: isOn ? series.color : COLORS.grid,
                  }}
                />
                {series.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ width: "100%", height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 28, right: 8, left: 0, bottom: 8 }}
            barGap={4}
            barCategoryGap="28%"
            onClick={handleChartClick}
          >
            <CartesianGrid vertical={false} stroke={COLORS.grid} />
            <XAxis
              dataKey="status"
              tick={<WrapTick />}
              tickLine={false}
              axisLine={{ stroke: COLORS.grid }}
              interval={0}
              height={52}
            />
            <YAxis
              yAxisId="num"
              tick={{ fontSize: 11, fill: COLORS.number }}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Number",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 11, fill: COLORS.number, fontWeight: 600 },
              }}
            />
            <YAxis
              yAxisId="area"
              orientation="right"
              tick={{ fontSize: 11, fill: COLORS.areaText }}
              tickLine={false}
              axisLine={false}
              label={{
                value: "Area (ha)",
                angle: 90,
                position: "insideRight",
                style: { fontSize: 11, fill: COLORS.areaText, fontWeight: 600 },
              }}
            />
            <Tooltip
              content={<ChartTooltip baseline={baseline} visible={visible} />}
              cursor={{ fill: "rgba(27, 110, 60, 0.06)" }} // soft tint, not a gray slab
            />
            <ReferenceLine yAxisId="num" y={0} stroke={COLORS.grid} />

            {SERIES.filter((series) => visible[series.key]).map((series) => (
              <Bar
                key={series.key}
                yAxisId={series.yAxisId}
                dataKey={series.key}
                name={series.label}
                radius={[6, 6, 0, 0]}
                maxBarSize={34}
                // keep zero-value statuses visible as a 2px stub
                minPointSize={(value: number) => (value === 0 ? 2 : 0)}
              >
                {rows.map((row) => (
                  <Cell
                    key={row.status}
                    fill={series.color}
                    fillOpacity={opacityFor(row, row[series.key])}
                    stroke={focused === row.status ? COLORS.focusRing : "none"}
                    strokeWidth={focused === row.status ? 2 : 0}
                    cursor="pointer"
                  />
                ))}
                <LabelList
                  dataKey={series.key}
                  position="top"
                  formatter={(value: any) =>
                    value === 0 ? (series.showZeroLabel ? "0" : "") : fmt(value)
                  }
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: series.labelColor,
                  }}
                />
              </Bar>
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* empty-status strip: makes the zeros informative instead of invisible */}
      {zeroRows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "6px",
            marginTop: "8px",
            paddingTop: "10px",
            borderTop: `1px dashed ${COLORS.grid}`,
            fontSize: "11px",
            color: COLORS.sub,
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            No records in:
          </Box>
          {zeroRows.map((row) => (
            <Box
              key={row.status}
              component="span"
              sx={{
                padding: "2px 8px",
                borderRadius: "999px",
                backgroundColor: "#F1F5F1",
                border: `1px solid ${COLORS.grid}`,
              }}
            >
              {row.status}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default memo(StatusBarChart);
